import React from 'react';
import { inr } from '../utils/format';
import printDocument from '../utils/printDoc';
import barcodeSvg from '../utils/barcodeSvg';
import { rupeesInWords } from '../utils/numberWords';
import { fileSrc } from '../utils/api';

// ── The printed bill ────────────────────────────────────────────────────────
// Designed once in Settings, saved as the `billTemplate` setting, and rendered
// from that template wherever a bill is printed. Like labels, it prints into its
// own document (see printDoc): a 58mm thermal roll and an A4 sheet have nothing
// in common with the app's stylesheet, and neither should have to fight it.

export const BILL_PAPERS = [
  { key: '58mm', label: '58 mm thermal roll', width: 58 },
  { key: '80mm', label: '80 mm thermal roll', width: 80 },
  { key: 'a5', label: 'A5 sheet', width: 148 },
  { key: 'a4', label: 'A4 sheet', width: 210 },
];

export const BILL_FIELDS = [
  ['logo', 'Logo'],
  ['address', 'Shop address'],
  ['gstin', 'GSTIN'],
  ['fssai', 'FSSAI licence'],
  ['phone', 'Phone'],
  ['customer', 'Customer name & phone'],
  ['shipTo', 'Delivery address'],
  ['cashier', 'Salesman'],
  ['mrpDisc', 'MRP and discount columns'],
  ['hsn', 'HSN / GST rate per line'],
  ['qtyTotal', 'Number of quantities'],
  ['savings', 'You saved (discount)'],
  ['payments', 'How it was paid'],
  ['dueNotice', 'Amount still due'],
  ['amountWords', 'Amount in words'],
  ['placeOfSupply', 'Place of supply line'],
  ['taxBreakup', 'Tax summary table'],
  ['terms', 'Terms & conditions'],
  ['barcode', 'Bill number as a barcode'],
  ['printedOn', 'Printed on / E & O E'],
];

export const DEFAULT_BILL = {
  paper: '80mm',
  title: 'Invoice',
  shopName: 'AVAKAAYA PICKLES HOUSE',
  gstin: '36BPMPS9249M3ZQ',
  fssai: '',
  placeOfSupply: 'Telangana',
  terms: '',
  footer: 'Thank you for shopping with us',
  fields: {
    logo: true, address: true, gstin: true, fssai: true, phone: true,
    customer: true, shipTo: true, cashier: true, mrpDisc: true, hsn: true,
    qtyTotal: true, savings: true, payments: true, dueNotice: true,
    amountWords: true, placeOfSupply: true, taxBreakup: true, terms: false,
    barcode: true, printedOn: true,
  },
};

export const normBill = (t) => {
  let o = t;
  if (typeof o === 'string') { try { o = JSON.parse(o); } catch (e) { o = null; } }
  if (!o || typeof o !== 'object' || Array.isArray(o)) o = {};
  return {
    paper: BILL_PAPERS.some((p) => p.key === o.paper) ? o.paper : DEFAULT_BILL.paper,
    title: typeof o.title === 'string' ? o.title : DEFAULT_BILL.title,
    shopName: typeof o.shopName === 'string' ? o.shopName : DEFAULT_BILL.shopName,
    gstin: typeof o.gstin === 'string' ? o.gstin : DEFAULT_BILL.gstin,
    fssai: typeof o.fssai === 'string' ? o.fssai : DEFAULT_BILL.fssai,
    placeOfSupply: typeof o.placeOfSupply === 'string' ? o.placeOfSupply : DEFAULT_BILL.placeOfSupply,
    terms: typeof o.terms === 'string' ? o.terms : DEFAULT_BILL.terms,
    footer: typeof o.footer === 'string' ? o.footer : DEFAULT_BILL.footer,
    fields: { ...DEFAULT_BILL.fields, ...(o.fields && typeof o.fields === 'object' ? o.fields : {}) },
  };
};

const paperOf = (t) => BILL_PAPERS.find((p) => p.key === t.paper) || BILL_PAPERS[1];
const isRoll = (t) => t.paper === '58mm' || t.paper === '80mm';

const esc = (v) => String(v == null ? '' : v)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// ── The bill as HTML ────────────────────────────────────────────────────────
// `sale` is the API's sale object; `taxSummary` its rate-wise GST rows.
export const LOGO_URL = '/avakaaya-logo.png';

// A branch with its own logo prints that; every other branch prints the shop's.
export const branchLogoUrl = (branch) => fileSrc(branch && branch.logoUrl) || LOGO_URL;

const logoDataUrls = new Map();
export async function loadLogo(url = LOGO_URL) {
  if (logoDataUrls.has(url)) return logoDataUrls.get(url);
  let src;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(res.statusText);
    const blob = await res.blob();
    src = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  } catch (e) {
    src = new URL(url, window.location.origin).href;
  }
  logoDataUrls.set(url, src);
  return src;
}

export function billHtml(template, sale, taxSummary = [], logoSrc = null) {
  const t = normBill(template);
  const f = t.fields;
  const items = sale.items || [];
  const payments = (sale.payments || []).filter((p) => Number(p.amount) > 0);
  const due = (sale.payments || []).filter((p) => p.mode === 'credit').reduce((s2, p) => s2 + Number(p.amount), 0);
  const discount = Number(sale.discount) || 0;
  const qtyTotal = items.reduce((s2, i) => s2 + Number(i.quantity || 0), 0);
  const when = new Date(sale.createdAt || Date.now());
  const dateStr = when.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = when.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

  const line = (l, r) => `<div class="kv"><span>${l}</span><span>${r}</span></div>`;

  // Address and phone belong to the branch that raised the bill, not to a line
  // typed once into a shared template: three shops, three addresses, one design.
  const branch = sale.location || {};
  const addressLines = String(branch.address || '').split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
  // A branch that registers separately overrides the business-wide numbers.
  const gstin = branch.gstin || t.gstin;
  const fssai = branch.fssai || t.fssai;
  // So may a branch trading under its own name and logo.
  const shopName = branch.shopName || t.shopName;
  const logo = logoSrc || branchLogoUrl(branch);
  const head = [
    f.logo ? `<img class="logo" src="${esc(logo)}" alt="" />` : '',
    `<div class="shop">${esc(shopName)}</div>`,
    t.title ? `<div class="title">${esc(t.title)}</div>` : '',
    f.address ? addressLines.map((a) => `<div class="sub">${esc(a)}</div>`).join('') : '',
    f.gstin && gstin ? `<div class="sub">GSTIN NO : ${esc(gstin)}</div>` : '',
    f.fssai && fssai ? `<div class="sub">FSSAI Lic. No : ${esc(fssai)}</div>` : '',
    f.phone && branch.phone ? `<div class="sub">Phone No : ${esc(branch.phone)}</div>` : '',
  ].filter(Boolean).join('');

  // Two columns, as the shop's bill has them: who and when on the left, the
  // document and who sold it on the right.
  const metaRows = [
    [f.customer && sale.customer ? `Name : ${esc(sale.customer.name)}` : '', `Date : ${esc(dateStr)}`],
    [f.customer && sale.customer && sale.customer.phone ? `Mobile : ${esc(sale.customer.phone)}` : '', `Time : ${esc(timeStr)}`],
    [`Invoice No. : ${esc(sale.saleNumber)}`, f.cashier && sale.seller ? `Salesman : ${esc(sale.seller.name)}` : ''],
  ].filter(([a, b]) => a || b)
    .map(([a, b]) => `<div class="meta-row"><span>${a}</span><span>${b}</span></div>`).join('');

  const rows = items.map((i, n) => {
    const qty = Number(i.quantity);
    const rate = Number(i.unitPrice);
    const disc = Number(i.discount) || 0;
    const sub = [];
    if (f.hsn) sub.push(`HSN: ${esc(i.hsnCode || '')}${Number(i.gstRate) ? ` GST ${Number(i.gstRate)}%` : ''}`);
    return `<tr>
      <td class="n">${n + 1}</td>
      <td>${esc(i.name)} ${esc(i.packLabel || '')}${sub.length ? `<div class="hsn">${sub.join(' ')}</div>` : ''}</td>
      <td class="r">${qty % 1 ? qty.toFixed(3) : qty}</td>
      ${f.mrpDisc ? `<td class="r">${rate.toFixed(2)}</td><td class="r">${disc.toFixed(2)}</td>` : ''}
      <td class="r">${Number(i.lineTotal).toFixed(2)}</td>
    </tr>`;
  }).join('');

  const totals = [
    line('TOTAL', esc(inr(sale.netTotal))),
    Number(sale.roundOff) !== 0 ? line('ROUND OFF', esc(inr(sale.roundOff))) : '',
    f.payments ? payments.map((p) => line(`BY ${esc(p.mode.toUpperCase())}`, esc(inr(p.amount)))).join('') : '',
    f.dueNotice && due > 0.005 ? line('AMOUNT DUE', esc(inr(due))) : '',
  ].filter(Boolean).join('');

  const taxTable = f.taxBreakup && taxSummary.length ? `
    <div class="tax-title">TAX SUMMARY</div>
    <table class="tax">
      <thead><tr><th>TAXABLE VALUE</th><th>CGST</th><th>SGST</th><th>Cess</th><th>IGST</th></tr></thead>
      <tbody>${taxSummary.map((g) => `<tr>
        <td>${Number(g.taxable).toFixed(2)}</td><td>${Number(g.cgst).toFixed(2)}</td>
        <td>${Number(g.sgst).toFixed(2)}</td><td>0.00</td><td>0.00</td></tr>`).join('')}</tbody>
    </table>` : '';

  // The name and mobile are already at the top, beside the date — this block is
  // only here for where the goods go, so it carries the address and nothing else.
  const customerBlock = f.shipTo && sale.shipToAddress ? `
    <div class="dashed"></div>
    <div class="cust">Customer Details</div>
    <div>Address : ${esc(sale.shipToAddress)}${sale.shipToPhone ? ` · ${esc(sale.shipToPhone)}` : ''}</div>` : '';

  return `
    <div class="bill">
      <div class="head">${head}</div>
      <div class="dashed"></div>
      <div class="meta">${metaRows}</div>
      <table class="items">
        <thead><tr>
          <th class="n">#</th><th>Item</th><th class="r">Qty</th>
          ${f.mrpDisc ? '<th class="r">MRP</th><th class="r">Disc</th>' : ''}
          <th class="r">Net</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="dashed"></div>
      <div class="totals">${totals}</div>
      ${f.qtyTotal ? `<div class="dashed"></div><div class="mid">NO OF QTY : ${qtyTotal % 1 ? qtyTotal.toFixed(3) : qtyTotal}</div>` : ''}
      ${f.savings && discount > 0 ? `<div class="mid saved">You Saved Rs. : ${Number(discount).toFixed(2)}</div>` : ''}
      <div class="dashed"></div>
      ${f.amountWords ? `<div class="words">${esc(rupeesInWords(sale.netTotal))}</div>` : ''}
      ${f.placeOfSupply && t.placeOfSupply ? `<div class="words">Prices are inclusive of all taxes - Place of Supply : ${esc(t.placeOfSupply)}</div>` : ''}
      ${taxTable}
      ${customerBlock}
      ${f.terms && t.terms ? `<div class="dashed"></div><div class="terms">${esc(t.terms)}</div>` : ''}
      ${f.barcode ? `<div class="mid barcode">${barcodeSvg(sale.saleNumber, { height: 34, width: 1.1, fontSize: 0, displayValue: false })}</div>` : ''}
      ${t.footer ? `<div class="mid foot">${esc(t.footer)}</div>` : ''}
      ${f.printedOn ? `<div class="printed"><span>Printed On: ${esc(dateStr)} ${esc(timeStr)}</span><span>E &amp; O E.</span></div>` : ''}
    </div>`;
}

export function billCss(template) {
  const t = normBill(template);
  const paper = paperOf(t);
  const roll = isRoll(t);
  // A roll has a width and no height: it feeds as far as the bill is long.
  const size = roll ? `${paper.width}mm auto` : t.paper.toUpperCase();
  const base = roll ? 11 : 12.5;
  return `
    @page { size: ${size}; margin: ${roll ? '2mm' : '10mm'}; }
    html, body { margin: 0; padding: 0; background: #fff; }
    body { font-family: ${roll ? "'Courier New', monospace" : "system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"};
           font-size: ${base}px; color: #000; line-height: 1.35; }
    .bill { width: 100%; }
    .dashed { border-top: 1px dashed #000; margin: 3px 0; }
    .head { text-align: center; }
    .logo { max-width: ${roll ? '26mm' : '38mm'}; margin: 0 auto 2px; display: block; }
    .shop { font-size: ${base + 3}px; font-weight: 700; }
    .title { font-weight: 700; margin: 2px 0; }
    .sub { font-size: ${base - 1}px; }
    .meta { font-size: ${base - 0.5}px; }
    .meta-row { display: flex; justify-content: space-between; gap: 6px; }
    .meta-row span:last-child { text-align: right; white-space: nowrap; }
    table { width: 100%; border-collapse: collapse; }
    .items { margin-top: 2px; }
    .items th { text-align: left; border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 2px 0; font-weight: 700; }
    .items td { padding: 2px 0; vertical-align: top; }
    .items .n { width: 1.6em; }
    .items .r, .tax td, .tax th { text-align: right; }
    .hsn { font-size: ${base - 2}px; font-style: italic; }
    .totals { font-weight: 700; }
    .kv { display: flex; justify-content: space-between; gap: 8px; }
    .mid { text-align: center; margin: 2px 0; }
    .saved { font-weight: 700; font-size: ${base + 1}px; }
    .words { margin: 2px 0; }
    .tax-title { text-align: center; font-weight: 700; margin-top: 4px; }
    .tax { border: 1px solid #000; margin-top: 2px; font-size: ${base - 1.5}px; }
    .tax th, .tax td { border: 1px solid #000; padding: 1px 3px; text-align: center; }
    .cust { font-weight: 700; }
    .terms { font-size: ${base - 2}px; }
    .barcode { margin: 6px 0 2px; }
    .barcode svg { max-width: 100%; height: auto; margin: 0 auto; display: block; }
    .foot { margin-top: 2px; }
    .printed { display: flex; justify-content: space-between; gap: 8px; margin-top: 6px; font-size: ${base - 2.5}px; }
  `;
}

export const printBill = async (template, sale, taxSummary) => {
  const logo = normBill(template).fields.logo ? await loadLogo(branchLogoUrl(sale.location)) : LOGO_URL;
  return printDocument({
    body: billHtml(template, sale, taxSummary, logo),
    css: billCss(template),
    title: sale.saleNumber || 'Bill',
  });
};

// ── The designer, for Settings ──────────────────────────────────────────────
const SAMPLE_SALE = {
  saleNumber: 'POS-20260828-0042', createdAt: new Date().toISOString(),
  location: {
    name: 'Chanda Nagar Store',
    address: 'LIG-75, 1st Phase, Dharmareddy Colony, Hyderabad\nHyderabad-500072',
    phone: '+91 95022 63250',
    fssai: '13322999000123',
  },
  seller: { name: 'Ravi' },
  customer: { name: 'Murali D', phone: '+919676453666' },
  shipToAddress: '12-3-45, Nizampet Road, KPHB Colony, Hyderabad 500072', shipToName: 'Home',
  items: [
    { name: 'Ajmeer Kalakand', packLabel: '1KG', quantity: 2, unitPrice: 680, lineTotal: 1360, discount: 80, hsnCode: '2106' },
    { name: 'Mango Pickle', packLabel: '500g', quantity: 1, unitPrice: 325, lineTotal: 325, discount: 0, hsnCode: '2001' },
  ],
  taxableAmount: 1604.76, cgst: 40.12, sgst: 40.12, roundOff: 0, discount: 80, netTotal: 1605,
  payments: [{ mode: 'cash', amount: 1000 }, { mode: 'upi', amount: 605 }],
};
const SAMPLE_TAX = [{ rate: 5, taxable: 1604.76, cgst: 40.12, sgst: 40.12 }];

export const BillDesigner = ({ value, onChange, disabled }) => {
  const t = normBill(value);
  const set = (patch) => onChange({ ...t, ...patch });
  const setField = (k) => onChange({ ...t, fields: { ...t.fields, [k]: !t.fields[k] } });
  const paper = paperOf(t);

  return (
    <div className="label-designer">
      <div className="label-designer-controls">
        <div className="flex gap" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ maxWidth: 220 }}><label>Paper</label>
            <select disabled={disabled} value={t.paper} onChange={(e) => set({ paper: e.target.value })}>
              {BILL_PAPERS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: 1, minWidth: 200 }}><label>Shop name</label>
            <input disabled={disabled} value={t.shopName} onChange={(e) => set({ shopName: e.target.value })} />
          </div>
        </div>
        <div className="row">
          <div className="field"><label>GSTIN</label><input disabled={disabled} value={t.gstin} onChange={(e) => set({ gstin: e.target.value })} /></div>
          <div className="field"><label>FSSAI licence</label><input disabled={disabled} value={t.fssai} onChange={(e) => set({ fssai: e.target.value })} inputMode="numeric" /></div>
          <div className="field" style={{ flex: 2 }}><label>&nbsp;</label>
            <span className="muted small">
              Address and phone come from the branch. A branch may also carry its own shop name, logo, GSTIN and FSSAI licence,
              which override these — set them per location in <strong>Settings › Locations</strong>.
            </span>
          </div>
        </div>
        <div className="row">
          <div className="field"><label>Document title</label><input disabled={disabled} value={t.title} onChange={(e) => set({ title: e.target.value })} placeholder="Invoice" /></div>
          <div className="field"><label>Place of supply</label><input disabled={disabled} value={t.placeOfSupply} onChange={(e) => set({ placeOfSupply: e.target.value })} /></div>
        </div>
        <div className="field"><label>Terms &amp; conditions</label>
          <input disabled={disabled} value={t.terms} onChange={(e) => set({ terms: e.target.value })} placeholder="Printed small at the foot of the bill" />
        </div>
        <div className="field"><label>Footer line</label>
          <input disabled={disabled} value={t.footer} onChange={(e) => set({ footer: e.target.value })} placeholder="Thank you! Visit again." />
        </div>
        <div className="flex gap" style={{ flexWrap: 'wrap', marginTop: '.4rem', gap: '.1rem .9rem' }}>
          <span className="muted small" style={{ alignSelf: 'center' }}>Print on the bill:</span>
          {BILL_FIELDS.map(([k, lbl]) => (
            <label key={k} className="chk"><input type="checkbox" disabled={disabled} checked={!!t.fields[k]} onChange={() => setField(k)} /> {lbl}</label>
          ))}
        </div>
        <p className="muted small" style={{ marginTop: '.6rem' }}>
          A roll feeds as far as the bill is long; a sheet prints one bill per page. The preview is the real thing —
          same markup, same stylesheet as the printer gets.
        </p>
        <p className="muted small" style={{ marginTop: '.6rem' }}>
          <strong>Printing without the preview dialog:</strong> a web page cannot skip it, but Chrome and Edge can be started
          with <span className="mono">--kiosk-printing</span>, which prints straight to the default printer. On the till machine,
          copy the browser shortcut, open its Properties, and add <span className="mono"> --kiosk-printing</span> to the end of the
          Target box — then set the label roll or bill printer as the Windows default. Nexus prints each document on its own
          page size, so the default printer is all it needs.
        </p>
      </div>
      <div className="label-designer-preview">
        <span className="muted small">Preview · {paper.label}</span>
        <div className="bill-preview" style={{ width: `${Math.min(paper.width, 100)}mm` }}>
          <style>{billCss(t).replace(/@page[^}]+}/, '')}</style>
          <div dangerouslySetInnerHTML={{ __html: billHtml(t, SAMPLE_SALE, SAMPLE_TAX) }} />
        </div>
      </div>
    </div>
  );
};
